import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { DatePivotParams } from "../entities/date_pivot_params";
import { useCustomer } from "../contexts/app/provider";
import dayjs from "../utils/dayjs";
import { isDatePivotParamsEqual } from "../utils/is_date_pivot_params_equal";

const BASE_PARAMS_KEYS = [
  "dateHierarchy",
  "measure",
  "segments",
  "minDate",
  "maxDate",
  "filters",
  "postFilters",
] as const;

// Helper to ensure date is a Date object
const ensureDate = (dateValue: any): Date => {
  if (dateValue instanceof Date) {
    return dateValue;
  }
  if (typeof dateValue === "string") {
    return dayjs.utc(dateValue).toDate();
  }
  return dayjs.utc().toDate();
};

// Helper to parse URL param value based on default type
const parseUrlValue = (
  urlValue: string,
  defaultValue: any
): string | number | boolean | any => {
  if (typeof defaultValue === "boolean") {
    return urlValue === "true";
  }
  if (typeof defaultValue === "number") {
    return Number(urlValue);
  }
  if (typeof defaultValue === "string") {
    return urlValue;
  }
  try {
    return JSON.parse(urlValue);
  } catch {
    return urlValue;
  }
};

// Build params object from URL, merging with defaults
const buildParamsFromUrl = <T extends DatePivotParams>(
  urlParams: URLSearchParams,
  defaults: T
): T => {
  const params: any = { ...defaults };

  // Handle base DatePivotParams properties
  params.dateHierarchy =
    urlParams.get("dateHierarchy") || defaults.dateHierarchy;
  params.measure = urlParams.get("measure") || defaults.measure;
  params.segments = urlParams.get("segments")
    ? JSON.parse(urlParams.get("segments")!)
    : defaults.segments;
  params.minDate = urlParams.get("minDate")
    ? dayjs.utc(urlParams.get("minDate")).toDate()
    : defaults.minDate ?? undefined;
  params.maxDate = urlParams.get("maxDate")
    ? dayjs.utc(urlParams.get("maxDate")).toDate()
    : defaults.maxDate ?? undefined;
  params.filters = urlParams.get("filters")
    ? JSON.parse(urlParams.get("filters")!)
    : defaults.filters;
  params.postFilters = urlParams.get("postFilters")
    ? JSON.parse(urlParams.get("postFilters")!)
    : defaults.postFilters;

  // Handle extended properties beyond DatePivotParams
  Object.entries(defaults).forEach(([key, defaultValue]) => {
    if (!BASE_PARAMS_KEYS.includes(key as any)) {
      const urlValue = urlParams.get(key);
      if (urlValue !== null) {
        params[key] = parseUrlValue(urlValue, defaultValue);
      }
    }
  });

  return params as T;
};

// Remove metadata fields (name, isDefault) from config
const removeMetadata = (config: any): any => {
  const { name, isDefault, ...params } = config;
  return params;
};

// Find matching pin config from stored params
const findMatchingPin = <T extends DatePivotParams>(
  storedParams: { paramsJSON: string },
  urlParams: T
): any | null => {
  try {
    const parsed = JSON.parse(storedParams.paramsJSON);

    // Handle array of configs (pins)
    if (Array.isArray(parsed)) {
      // Try to find a config that matches the URL params
      return (
        parsed.find((c) => {
          const configParams = removeMetadata(c);
          return isDatePivotParamsEqual(urlParams, configParams as T);
        }) || null
      );
    }

    // Single config object (backward compatibility)
    return parsed || null;
  } catch {
    return null;
  }
};

// Get default pin from array of configs
const getDefaultPin = (configs: any[]): any | null => {
  return (
    configs.find((c) => c.isDefault === true) ||
    configs.find((c) => !c.name || c.name === "") ||
    configs[0] ||
    null
  );
};

export const useParams = <T extends DatePivotParams = DatePivotParams>(
  defaultConfig: T,
  datasetName: string,
  transformId: string
) => {
  const { customerSettings, loading: customerSettingsLoading } = useCustomer();
  const { search } = useLocation();
  const navigate = useNavigate();

  const [viewParams, setViewParams] = useState<T | null>(null);
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const hasInitializedRef = useRef(false);
  const currentViewParamsRef = useRef<T | null>(null);
  const previousUrlParamsRef = useRef<string | null>(null);

  useEffect(() => {
    if (!customerSettings) {
      return;
    }

    const viewKey = `${datasetName}|${transformId}`;
    const storedParams = (customerSettings.preloadDatasets || []).find(
      (p) => p.viewDatasetTransform === viewKey
    );

    // Check if URL params have changed (e.g., browser back/forward, direct URL change)
    const currentUrlParamsString = params.toString();
    const urlParamsChanged =
      previousUrlParamsRef.current !== null &&
      previousUrlParamsRef.current !== currentUrlParamsString;

    // Build params from URL - this represents the current state
    const paramsFromUrl = buildParamsFromUrl(params, defaultConfig);

    // Find matching pin config based on URL params
    let matchedConfig: any = null;
    if (storedParams?.paramsJSON) {
      matchedConfig = findMatchingPin(storedParams, paramsFromUrl);

      // If no match found, try to use current params (prevents refresh when unpinning)
      if (!matchedConfig && currentViewParamsRef.current && !urlParamsChanged) {
        // Use current params to prevent refresh when unpinning (but only if URL hasn't changed)
        const newParams = buildParamsFromUrl(
          params,
          currentViewParamsRef.current
        );
        const paramsWouldBeSame = isDatePivotParamsEqual(
          currentViewParamsRef.current,
          newParams
        );

        if (paramsWouldBeSame && hasInitializedRef.current) {
          // Skip update - params haven't actually changed
          previousUrlParamsRef.current = currentUrlParamsString;
          return;
        }
      }

      // If still no match, try to get default pin from array
      if (!matchedConfig && storedParams.paramsJSON) {
        try {
          const parsed = JSON.parse(storedParams.paramsJSON);
          if (Array.isArray(parsed)) {
            matchedConfig = getDefaultPin(parsed);
          }
        } catch {
          // Invalid JSON, ignore
        }
      }
    }

    // Start with matched config (or default), then override with URL params
    // URL params always take precedence
    const baseConfig = matchedConfig
      ? removeMetadata(matchedConfig)
      : defaultConfig;
    const newParams = buildParamsFromUrl(params, baseConfig as T);

    // Check if params would actually change
    const paramsWouldBeSame =
      currentViewParamsRef.current !== null &&
      isDatePivotParamsEqual(currentViewParamsRef.current, newParams);

    // Skip update if params haven't changed and we've already initialized
    // This prevents unnecessary reloads when saving/renaming/creating/unpinning pins
    // However, always update if URL params changed externally
    if (!urlParamsChanged && hasInitializedRef.current && paramsWouldBeSame) {
      previousUrlParamsRef.current = currentUrlParamsString;
      return;
    }

    // Update state
    hasInitializedRef.current = true;
    previousUrlParamsRef.current = currentUrlParamsString;
    setViewParams(newParams);
  }, [customerSettings, datasetName, defaultConfig, params, transformId]);

  // Update the ref whenever viewParams changes (for use in the effect above)
  useEffect(() => {
    currentViewParamsRef.current = viewParams;
  }, [viewParams]);

  // Sync viewParams to URL
  useEffect(() => {
    if (!viewParams) {
      return;
    }

    const urlParams = new URLSearchParams();

    // Handle base DatePivotParams properties
    urlParams.set("dateHierarchy", viewParams.dateHierarchy);
    if (viewParams.measure) {
      urlParams.set("measure", viewParams.measure);
    }
    if (viewParams.segments) {
      urlParams.set("segments", JSON.stringify(viewParams.segments));
    }

    // Ensure dates are Date objects before calling toISOString
    if (viewParams.minDate) {
      const minDate = ensureDate(viewParams.minDate);
      urlParams.set("minDate", minDate.toISOString());
    }
    if (viewParams.maxDate) {
      const maxDate = ensureDate(viewParams.maxDate);
      urlParams.set("maxDate", maxDate.toISOString());
    }

    urlParams.set("filters", JSON.stringify(viewParams.filters));
    if (viewParams.postFilters) {
      urlParams.set("postFilters", JSON.stringify(viewParams.postFilters));
    }

    // Handle extended properties beyond DatePivotParams
    Object.entries(viewParams).forEach(([key, value]) => {
      if (!BASE_PARAMS_KEYS.includes(key as any) && value !== undefined) {
        if (typeof value === "boolean") {
          urlParams.set(key, String(value));
        } else if (typeof value === "string" || typeof value === "number") {
          urlParams.set(key, String(value));
        } else if (value !== null) {
          urlParams.set(key, JSON.stringify(value));
        }
      }
    });

    navigate(`?${urlParams.toString()}`, { replace: true });
  }, [navigate, viewParams]);

  return {
    loading: customerSettingsLoading,
    viewParams,
    setViewParams,
  };
};
